import React, { useEffect, useRef, useState } from "react";
import { adminGetJson, adminPostJson } from "./adminApi";
import { AdminSpinner } from "./AdminLoadingUi";
import { cardStyle, chipStyle, inputStyle, muted, primaryButtonStyle, useElapsed } from "./AdminIntelligenceShared";

function statusColor(status) {
  switch (String(status || "").toUpperCase()) {
    case "NEW": return { background: "#dbeafe", color: "#1d4ed8" };
    case "ONGOING": return { background: "#fef3c7", color: "#b45309" };
    case "INTENSIFYING": return { background: "#fee2e2", color: "#b91c1c" };
    case "RESOLVED": return { background: "#dcfce7", color: "#15803d" };
    default: return { background: "#e5e7eb", color: "#334155" };
  }
}

function FindingCard({ finding, onEntityClick }) {
  const badge = statusColor(finding.status);
  return (
    <div style={{ ...cardStyle, borderColor: badge.background }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
        <div style={{ fontWeight: 900, fontSize: 18 }}>{finding.headline}</div>
        <span style={{ ...chipStyle, background: badge.background, color: badge.color, borderColor: badge.background }}>{finding.status || "NEW"}</span>
      </div>
      <div style={{ display: "grid", gap: 8, fontSize: 14, lineHeight: 1.6 }}>
        {finding.the_math ? <div><strong>The math:</strong> {finding.the_math}</div> : null}
        {finding.the_numbers ? <div><strong>The numbers:</strong> {finding.the_numbers}</div> : null}
        {finding.who_is_involved ? <div><strong>Who is involved:</strong> {finding.who_is_involved}</div> : null}
        {finding.alternative_explanations ? <div><strong>Alternative explanations:</strong> {finding.alternative_explanations}</div> : null}
        {finding.what_would_confirm_it ? <div><strong>What would confirm it:</strong> {finding.what_would_confirm_it}</div> : null}
      </div>
      {(finding.entities || []).length ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          {finding.entities.map((entity, entityIdx) => (
            <button key={`${entity.entity_type || "entity"}-${entity.entity_id || entityIdx}-${entityIdx}`} style={{ ...chipStyle, cursor: "pointer" }} onClick={() => onEntityClick(entity.entity_type || "seller", entity.entity_id || String(entity))}>
              {entity.entity_id || String(entity)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function parseFindings(propMessages) {
  if (!propMessages?.length) return [];
  const all = [];
  for (const msg of propMessages) {
    if (msg.role !== "assistant") continue;
    let findings = null;
    if (msg.raw_data) {
      const raw = typeof msg.raw_data === "string" ? (() => { try { return JSON.parse(msg.raw_data); } catch (_) { return null; } })() : msg.raw_data;
      if (Array.isArray(raw?.findings) && raw.findings.length) findings = raw.findings;
    }
    if (!findings && msg.content) {
      try {
        const parsed = JSON.parse(msg.content);
        if (Array.isArray(parsed) && parsed.length) findings = parsed;
      } catch (_) {}
    }
    if (findings) all.push(...findings);
  }
  return all;
}

export default function AdminInvestigatorTab({ sessionId, session, messages: propMessages, schemaOptions, sessionLoading, onSessionStarted, onSessionResolved, onEntityClick, onNewSession }) {
  const [guidance, setGuidance] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [depth, setDepth] = useState("surface");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [localFindings, setLocalFindings] = useState([]);
  const ownGenerateRef = useRef(false);
  const activeGenerateRef = useRef(null);
  const scrollRef = useRef(null);
  const elapsed = useElapsed(submitting);

  const bounds = schemaOptions?.dateBounds?.global || { min: "", max: "" };

  const messagesReady = Boolean(sessionId && session?.id === sessionId);
  const propFindings = messagesReady ? parseFindings(propMessages) : [];
  const propError = messagesReady && session?.status === "error" ? (session.error_message || "Investigation failed.") : null;

  // Sync from parent when the user navigates to a past session via the sidebar.
  useEffect(() => {
    if (ownGenerateRef.current) {
      ownGenerateRef.current = false;
      return;
    }
    activeGenerateRef.current = null;
    setLocalFindings([]);
    setError("");
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cancel polling on unmount.
  useEffect(() => { return () => { activeGenerateRef.current = null; }; }, []);

  const displayFindings = (() => {
    const merged = localFindings.length ? [...propFindings, ...localFindings] : propFindings;
    const seen = new Set();
    return merged.filter((f) => {
      const key = (f.headline || "") + "||" + (f.the_math || "");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();
  const displayError = error || (!submitting && !localFindings.length ? propError : null);
  const showLoadingOverlay = (sessionLoading || (sessionId && !messagesReady)) && !submitting && !localFindings.length;
  const isEmpty = !displayFindings.length && !submitting && !displayError;

  // Scroll to bottom when new findings appear or generation starts.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [localFindings.length, submitting]);

  async function runScan() {
    if (submitting) return;
    setSubmitting(true);
    setError("");

    let resolvedSessionId = sessionId || null;
    try {
      const postRes = await adminPostJson("/api/admin/intelligence/investigator", {
        session_id: sessionId || null,
        guidance,
        date_from: dateFrom,
        date_to: dateTo,
        depth,
      });
      resolvedSessionId = postRes.session_id;

      if (resolvedSessionId !== sessionId) {
        ownGenerateRef.current = true;
        onSessionStarted("tab4", resolvedSessionId);
      }

      activeGenerateRef.current = resolvedSessionId;
      let done = false;
      while (!done) {
        await new Promise((r) => setTimeout(r, 3000));
        if (activeGenerateRef.current !== resolvedSessionId) return;
        try {
          const res = await adminGetJson(
            `/api/admin/intelligence/sessions/${encodeURIComponent(resolvedSessionId)}?advance=false`
          );
          if (activeGenerateRef.current !== resolvedSessionId) return;

          if (res.session?.status === "complete") {
            done = true;
            const newFindings = parseFindings(res.messages || []);
            if (newFindings.length) setLocalFindings((prev) => [...prev, ...newFindings]);
            onSessionResolved("tab4", resolvedSessionId);
          } else if (res.session?.status === "error") {
            done = true;
            setError(res.session.error_message || "Investigation failed. Please try again.");
            onSessionResolved("tab4", resolvedSessionId);
          }
        } catch (_pollErr) {}
      }
    } catch (err) {
      setError(err.message || String(err));
      if (resolvedSessionId) onSessionResolved("tab4", resolvedSessionId);
    } finally {
      if (activeGenerateRef.current === resolvedSessionId) activeGenerateRef.current = null;
      setSubmitting(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", position: "relative" }}>

      {/* ── Sidebar-navigation loading overlay ── */}
      {showLoadingOverlay ? (
        <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.82)", zIndex: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <AdminSpinner loading />
          <div style={{ fontWeight: 700, color: "#334155" }}>Loading investigation…</div>
        </div>
      ) : null}

      {/* ── Scrollable content area ── */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "8px 8px 0 8px" }}>
        <div style={{ display: "grid", gap: 14, paddingBottom: 8, maxWidth: 960 }}>

          {/* Welcome state */}
          {isEmpty ? (
            <div style={cardStyle}>
              <div style={{ fontSize: 28, fontWeight: 900, marginBottom: 10 }}>Investigator</div>
              <div style={{ ...muted, fontSize: 15, lineHeight: 1.8, maxWidth: 760 }}>
                Run statistical scans across India's government procurement database to surface anomalies, concentration patterns, and pricing outliers.
              </div>
              <div style={{ marginTop: 16, display: "grid", gap: 10, maxWidth: 760 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#334155" }}>How it works</div>
                <div style={{ ...muted, fontSize: 14, lineHeight: 1.7 }}>
                  1. Hit <strong>Investigate</strong> — the system pulls data from contracts, GeM bids, results, and CPPP awards, runs statistical analysis (HHI concentration, Z-score outliers, trend break detection), then sends the strongest patterns to AI for deep reasoning.
                </div>
                <div style={{ ...muted, fontSize: 14, lineHeight: 1.7 }}>
                  2. Each finding tells you <strong>the math</strong> behind the pattern, <strong>who is involved</strong>, <strong>alternative explanations</strong>, and <strong>what would confirm it</strong>.
                </div>
                <div style={{ marginTop: 10, fontSize: 14, fontWeight: 700, color: "#334155" }}>Controls (all optional)</div>
                <div style={{ ...muted, fontSize: 14, lineHeight: 1.7 }}>
                  <strong>Guidance</strong> — steer the investigation toward a specific area. Examples: "repeat winners in IT hardware", "pricing variance in pharma", "vendor switches in defence". Leave blank to scan everything.
                </div>
                <div style={{ ...muted, fontSize: 14, lineHeight: 1.7 }}>
                  <strong>From / To dates</strong> — narrow the time window. Filters contracts by contract date, bids/results by end date, and CPPP awards by award date. Leave blank to scan the full database.
                </div>
                <div style={{ ...muted, fontSize: 14, lineHeight: 1.7 }}>
                  <strong>Surface vs Deep</strong> — Surface pulls top 30 rows per data dimension and runs quick analysis. Deep pulls top 90 rows for a more thorough scan — takes longer but catches subtler patterns.
                </div>
              </div>
            </div>
          ) : null}

          {/* ── Results: finding cards (older first, newer at bottom) ── */}
          {displayFindings.length ? (
            <>
              <div style={{ ...muted, fontWeight: 700, fontSize: 14 }}>{displayFindings.length} findings</div>
              {displayFindings.map((finding, idx) => (
                <FindingCard key={`${finding.headline || idx}-${idx}`} finding={finding} onEntityClick={onEntityClick} />
              ))}
            </>
          ) : null}

          {/* Generating indicator — at bottom so it's visible near the input bar */}
          {submitting ? (
            <div style={{ ...cardStyle, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700 }}>
                <AdminSpinner loading />
                <span>Running {depth === "deep" ? "deep" : "surface"} investigation…</span>
              </div>
              <div style={{ ...muted, marginTop: 6 }}>Scanning procurement data &amp; building statistical flags… {elapsed}s</div>
            </div>
          ) : null}

          {/* Error — at bottom */}
          {displayError ? (
            <div style={{ ...cardStyle, color: "#b91c1c", fontWeight: 700 }}>Error: {displayError}</div>
          ) : null}
        </div>
      </div>

      {/* ── Input bar — always pinned at bottom, all controls inline ── */}
      <div style={{ borderTop: "1px solid #e5e7eb", background: "#fff", padding: "6px 10px", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <input
            style={{ ...inputStyle, flex: 1, minHeight: 36, minWidth: 160, fontSize: 13 }}
            placeholder="Guidance — e.g. repeat winners in IT, pricing variance… or blank"
            value={guidance}
            onChange={(e) => setGuidance(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runScan(); }}
          />
          <input type="date" style={{ ...inputStyle, minHeight: 36, padding: "4px 6px", fontSize: 12, width: 130 }} min={bounds?.min || ""} max={bounds?.max || ""} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="From date" />
          <input type="date" style={{ ...inputStyle, minHeight: 36, padding: "4px 6px", fontSize: 12, width: 130 }} min={bounds?.min || ""} max={bounds?.max || ""} value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="To date" />
          <select style={{ ...inputStyle, minHeight: 36, padding: "4px 6px", fontSize: 12, width: 90 }} value={depth} onChange={(e) => setDepth(e.target.value)} title="Scan depth">
            <option value="surface">Surface</option>
            <option value="deep">Deep</option>
          </select>
          <button
            style={{ ...primaryButtonStyle, minWidth: 80, height: 36, flexShrink: 0, fontSize: 13 }}
            onClick={runScan}
            disabled={submitting}
          >
            {submitting ? "Running…" : "Investigate"}
          </button>
          <button style={{ border: 0, background: "transparent", padding: 0, color: "#2563eb", cursor: "pointer", fontWeight: 700, fontSize: 12, flexShrink: 0, whiteSpace: "nowrap" }} onClick={onNewSession}>+ New</button>
        </div>
      </div>
    </div>
  );
}
